import type * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Textarea from '@cloudscape-design/components/textarea';
import { toast } from '@/lib/notifications';
import type { TenantId } from '@etiquetachef/types';
import { restoreTenantCallable } from '@/lib/callables';

type Props = {
  open: boolean;
  tenantId: TenantId;
  tenantName: string;
  onClose: () => void;
};

/**
 * Dialog de restauração de tenant DELETED → SUSPENDED_ADMIN.
 *
 * Origem: sem issue — commit 3d9a9f21c, 2026-05-05 10:05 +0200 (Sprint 19 PR
 * 3d: UI para encerrar/restaurar tenant).
 *
 * Por quê: desfaz um encerramento dentro da janela de retenção. A
 * subscription **não volta** — owner precisa reativar via paywall do
 * cliente. Apenas o acesso operacional é reaberto. Apenas motivo
 * obrigatório (audit), sem confirmação dupla — restore é reversível.
 *
 * Contrato: chama `restoreTenant` com `{ tenantId, reason }`. O callable é
 * idempotente (tenant que não está `DELETED` volta sem mudar nada). Erro vira
 * `Alert` dentro do modal com a mensagem do callable (#1021; toast ficava
 * atrás do overlay).
 *
 * Usado por: `features/billing/BillingActions.tsx` (aba Cobrança do
 * `TenantDetailPage`).
 *
 * Arquitetura: lógica em `apps/functions/src/admin/restoreTenant.ts`.
 */
export function RestoreTenantDialog({
  open,
  tenantId,
  tenantName,
  onClose,
}: Props): React.JSX.Element {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function close(): void {
    setReason('');
    setError(null);
    onClose();
  }

  async function handleSubmit(): Promise<void> {
    if (reason.trim().length === 0) {
      setError(t('tenantDetail.actions.reasonRequired'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await restoreTenantCallable({ tenantId, reason: reason.trim() });
      toast.success(t('tenantDetail.actions.restoreDialog.success'));
      close();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('tenantDetail.actions.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      visible={open}
      // #1021 (2ª auditoria 2026-10-25 15:52 +0100): Esc/X/clique fora fechavam o modal com a chamada
      // em voo, e a falha caía no `Alert` de um modal já fechado — ninguém a via (o Cancelar já
      // ficava desligado; o `onDismiss` não).
      onDismiss={(): void => {
        if (!submitting) close();
      }}
      header={t('tenantDetail.actions.restoreDialog.title')}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={close} disabled={submitting}>
              {t('tenantDetail.actions.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={(): void => {
                void handleSubmit();
              }}
              disabled={submitting || reason.trim().length === 0}
              loading={submitting}
            >
              {t('tenantDetail.actions.restoreDialog.confirm')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box variant="p" color="text-body-secondary">
          {t('tenantDetail.actions.restoreDialog.description', { name: tenantName })}
        </Box>
        <Alert type="warning">{t('tenantDetail.actions.restoreDialog.warning')}</Alert>

        <FormField label={t('tenantDetail.actions.reasonLabel')}>
          <Textarea
            value={reason}
            onChange={({ detail }): void => {
              setReason(detail.value);
              // #1021 (2026-10-25 15:00 +0100): digitar o motivo tira o "motivo obrigatório" do Alert.
              setError(null);
            }}
            rows={3}
            disabled={submitting}
            placeholder={t('tenantDetail.actions.reasonPlaceholder')}
          />
        </FormField>
        {/* Auditoria 2026-10-25 14:29 +0100 (#1021): o erro ia só por toast, que fica atrás do overlay
            do Modal (mesmo achado do CRM, #174). Fica dentro do modal, junto do footer. */}
        {error !== null && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </Modal>
  );
}
